import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Users, UserPlus, Mail, Shield, Trash2, MoreHorizontal, Loader2, AlertCircle, Clock, RefreshCw, X } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useToast } from '@/components/ui/toast';
import { useProjects, useTeamMembers } from '@/hooks';
import { useTeamInvitations } from '@/hooks/useInvitations';
import { usePlanLimits } from '@/hooks/usePlanLimits';
import { PlanLimitWarning, UsageBadge } from '@/components/billing';

type Role = 'admin' | 'member';

/**
 * Get initials from an email address
 */
function getInitials(email: string): string {
  // Try to get initials from the email local part
  const localPart = email.split('@')[0];
  const parts = localPart.split(/[._-]/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return localPart.slice(0, 2).toUpperCase();
}

/**
 * Format a date for display
 */
function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Role badge component
 */
function RoleBadge({ role }: { role: Role }) {
  switch (role) {
    case 'admin':
      return <Badge>Admin</Badge>;
    case 'member':
      return <Badge variant="outline">Member</Badge>;
    default:
      return <Badge variant="outline">{role}</Badge>;
  }
}

/**
 * Page for managing project team members
 */
export function TeamPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const { projects, isLoadingProjects } = useProjects();

  // Find the project to get its team_id
  const project = projects.find((p) => p.id === projectId);
  const teamId = project?.team_id ?? null;

  const {
    team,
    members,
    isLoading: isLoadingTeam,
    error: teamError,
    inviteMember,
    isInviting,
    updateMemberRole,
    removeMember,
  } = useTeamMembers(teamId);

  // Pending invitations
  const {
    invitations,
    isLoading: isLoadingInvitations,
    revokeInvitation,
    isRevoking,
    resendInvitation,
    isResending,
  } = useTeamInvitations(teamId);

  // Plan limits
  const { membersUsage, canInviteMember, refetchUsage } = usePlanLimits(teamId);
  const isAtMemberLimit = !canInviteMember();

  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<Role>('member');
  const [actionInProgress, setActionInProgress] = useState<string | null>(null);

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteEmail || isAtMemberLimit) return;

    try {
      await inviteMember({ email: inviteEmail, role: inviteRole });
      toast.success(`Invitation sent to ${inviteEmail}`);
      setInviteEmail('');
      refetchUsage(); // Update usage counts
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to send invitation';
      toast.error(message);
    }
  };

  const handleRevokeInvitation = async (invitationId: string) => {
    setActionInProgress(invitationId);
    try {
      await revokeInvitation(invitationId);
      toast.success('Invitation revoked');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to revoke invitation';
      toast.error(message);
    } finally {
      setActionInProgress(null);
    }
  };

  const handleResendInvitation = async (invitationId: string) => {
    setActionInProgress(invitationId);
    try {
      await resendInvitation(invitationId);
      toast.success('Invitation resent');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to resend invitation';
      toast.error(message);
    } finally {
      setActionInProgress(null);
    }
  };

  const handleRoleChange = async (memberId: string, newRole: Role) => {
    try {
      await updateMemberRole({ userId: memberId, role: newRole });
      toast.success(`Member role updated to ${newRole}`);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update role';
      toast.error(message);
    }
  };

  const handleRemoveMember = async (memberId: string) => {
    try {
      await removeMember(memberId);
      toast.success('Member removed from team');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to remove member';
      toast.error(message);
    }
  };

  // Show message if no project selected
  if (!projectId) {
    return (
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Card className="glass border-border/50">
            <CardContent className="p-12 text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-muted/50 mb-4">
                <Users className="h-6 w-6 text-muted-foreground" />
              </div>
              <p className="text-muted-foreground">
                Select a project from the sidebar to manage team members.
              </p>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  // Show loading state
  if (isLoadingProjects || isLoadingTeam) {
    return (
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
        >
          <Card className="glass border-border/50 overflow-hidden">
            <CardContent className="p-0">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="flex items-center gap-4 px-6 py-4 border-b border-border/50 last:border-b-0">
                  <div className="h-10 w-10 rounded-full bg-muted/50 animate-shimmer" />
                  <div className="space-y-2 flex-1">
                    <div className="h-4 w-40 bg-muted/50 rounded animate-shimmer" />
                    <div className="h-3 w-24 bg-muted/30 rounded animate-shimmer" />
                  </div>
                  <div className="h-6 w-16 bg-muted/30 rounded animate-shimmer" />
                </div>
              ))}
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  // Show error state
  if (teamError) {
    return (
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Card className="glass border-border/50">
            <CardContent className="p-12 text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-destructive/10 mb-4">
                <AlertCircle className="h-6 w-6 text-destructive" />
              </div>
              <p className="text-destructive">Failed to load team members. Please try again.</p>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  // Show message if project not found or no team
  if (!project || !teamId) {
    return (
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Card className="glass border-border/50">
            <CardContent className="p-12 text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-muted/50 mb-4">
                <Users className="h-6 w-6 text-muted-foreground" />
              </div>
              <p className="text-muted-foreground">
                Project not found or no team associated.
              </p>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <motion.div 
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="flex items-center gap-3 mb-6"
      >
        <div className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-primary/10">
          <Users className="h-5 w-5 text-primary" />
        </div>
        <div>
          <h1 className="text-xl font-semibold tracking-tight text-foreground">Team</h1>
          <p className="text-sm text-muted-foreground">
            {team?.name ? `${team.name} - ` : ''}Manage team members and permissions
          </p>
        </div>
        <div className="ml-auto">
          {membersUsage ? (
            <UsageBadge usage={membersUsage} />
          ) : (
            <span className="text-xs text-muted-foreground bg-muted/50 px-2 py-0.5 rounded-full">
              {members.length} member{members.length !== 1 ? 's' : ''}
            </span>
          )}
        </div>
      </motion.div>

      {/* Limit warning banner */}
      {membersUsage && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-4"
        >
          <PlanLimitWarning
            resourceType="members"
            usage={membersUsage}
            variant="banner"
            onUpgradeClick={() => navigate('/billing')}
          />
        </motion.div>
      )}

      {/* Invite Member Card */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.1 }}
      >
        <Card className="glass border-border/50 mb-6">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-base">
              <UserPlus className="h-4 w-4 text-primary" />
              Invite Member
            </CardTitle>
            <CardDescription>
              {isAtMemberLimit
                ? 'You have reached your team member limit. Upgrade to add more members.'
                : 'Send an invitation to add a new team member to this project'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleInvite} className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <Label htmlFor="invite-email" className="sr-only">
                  Email address
                </Label>
                <div className="relative group">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground transition-colors group-focus-within:text-primary" />
                  <Input
                    id="invite-email"
                    type="email"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    placeholder="Enter email address"
                    className="pl-9 bg-muted/30 border-transparent focus:border-primary/50 focus:bg-muted/50 transition-all"
                    required
                    disabled={isAtMemberLimit}
                  />
                </div>
              </div>
              <div className="w-full sm:w-40">
                <Label htmlFor="invite-role" className="sr-only">
                  Role
                </Label>
                <select
                  id="invite-role"
                  value={inviteRole}
                  onChange={(e) => setInviteRole(e.target.value as Role)}
                  className="flex h-10 w-full rounded-md bg-muted/30 border-transparent px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 transition-all focus:border-primary/50 focus:bg-muted/50 disabled:opacity-50"
                  disabled={isAtMemberLimit}
                >
                  <option value="member">Member</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <motion.div whileTap={{ scale: 0.95 }}>
                <Button
                  type="submit"
                  disabled={isInviting || !inviteEmail || isAtMemberLimit}
                  className="shadow-lg shadow-primary/20 hover:shadow-primary/30 transition-shadow"
                >
                  {isInviting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Inviting...
                    </>
                  ) : (
                    <>
                      <UserPlus className="h-4 w-4 mr-2" />
                      Invite
                    </>
                  )}
                </Button>
              </motion.div>
            </form>
          </CardContent>
        </Card>
      </motion.div>

      {/* Team Members Card */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.2 }}
      >
        <Card className="glass border-border/50 overflow-hidden">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-base">
              <Users className="h-4 w-4 text-primary" />
              Team Members
            </CardTitle>
            <CardDescription>
              {members.length} member{members.length !== 1 ? 's' : ''} in this team
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <AnimatePresence mode="wait">
              {members.length === 0 ? (
                <motion.div
                  key="empty"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="p-12 text-center"
                >
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-muted/50 mb-4">
                    <Users className="h-6 w-6 text-muted-foreground" />
                  </div>
                  <p className="text-muted-foreground">No team members found.</p>
                </motion.div>
              ) : (
                <motion.div
                  key="table"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  <Table>
                    <TableHeader>
                      <TableRow className="border-border/50 hover:bg-transparent">
                        <TableHead className="text-muted-foreground">Member</TableHead>
                        <TableHead className="text-muted-foreground">Role</TableHead>
                        <TableHead className="text-muted-foreground">Joined</TableHead>
                        <TableHead className="w-[70px] text-muted-foreground">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {members.map((member, index) => (
                        <motion.tr
                          key={member.user_id}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.2, delay: index * 0.05 }}
                          whileHover={{ backgroundColor: 'hsl(var(--muted) / 0.1)' }}
                          className="border-border/50 transition-colors"
                        >
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <motion.div whileHover={{ scale: 1.05 }} transition={{ duration: 0.2 }}>
                                <Avatar className="h-9 w-9">
                                  <AvatarFallback className="bg-primary/10 text-primary text-sm font-medium">
                                    {getInitials(member.email)}
                                  </AvatarFallback>
                                </Avatar>
                              </motion.div>
                              <div>
                                <div className="font-medium text-foreground">{member.email}</div>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <RoleBadge role={member.role as Role} />
                          </TableCell>
                          <TableCell className="text-muted-foreground text-sm">
                            {formatDate(member.joined_at)}
                          </TableCell>
                          <TableCell>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <motion.div whileTap={{ scale: 0.95 }}>
                                  <Button variant="ghost" size="icon" className="h-8 w-8 hover:bg-muted/50">
                                    <MoreHorizontal className="h-4 w-4" />
                                    <span className="sr-only">Open menu</span>
                                  </Button>
                                </motion.div>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="glass">
                                <DropdownMenuItem
                                  onClick={() => handleRoleChange(member.user_id, member.role === 'admin' ? 'member' : 'admin')}
                                >
                                  <Shield className="h-4 w-4 mr-2" />
                                  {member.role === 'admin' ? 'Demote to Member' : 'Promote to Admin'}
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() => handleRemoveMember(member.user_id)}
                                  className="text-destructive focus:text-destructive"
                                >
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  Remove from Team
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </motion.tr>
                      ))}
                    </TableBody>
                  </Table>
                </motion.div>
              )}
            </AnimatePresence>
          </CardContent>
        </Card>
      </motion.div>

      {/* Pending Invitations Card */}
      {!isLoadingInvitations && invitations.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.3 }}
          className="mt-6"
        >
          <Card className="glass border-border/50 overflow-hidden">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2 text-base">
                <Clock className="h-4 w-4 text-amber-500" />
                Pending Invitations
              </CardTitle>
              <CardDescription>
                {invitations.length} pending invitation{invitations.length !== 1 ? 's' : ''} waiting to be accepted
              </CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="border-border/50 hover:bg-transparent">
                    <TableHead className="text-muted-foreground">Email</TableHead>
                    <TableHead className="text-muted-foreground">Role</TableHead>
                    <TableHead className="text-muted-foreground">Invited</TableHead>
                    <TableHead className="text-muted-foreground">Expires</TableHead>
                    <TableHead className="w-[100px] text-muted-foreground">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invitations.map((invitation, index) => {
                    const isExpired = new Date(invitation.expires_at) < new Date();
                    const isProcessing = actionInProgress === invitation.id;

                    return (
                      <motion.tr
                        key={invitation.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.2, delay: index * 0.05 }}
                        whileHover={{ backgroundColor: 'hsl(var(--muted) / 0.1)' }}
                        className="border-border/50 transition-colors"
                      >
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <motion.div whileHover={{ scale: 1.05 }} transition={{ duration: 0.2 }}>
                              <Avatar className="h-9 w-9">
                                <AvatarFallback className="bg-amber-500/10 text-amber-500 text-sm font-medium">
                                  {getInitials(invitation.email)}
                                </AvatarFallback>
                              </Avatar>
                            </motion.div>
                            <div>
                              <div className="font-medium text-foreground">{invitation.email}</div>
                              <div className="text-xs text-muted-foreground">
                                Invited by {invitation.invited_by_email}
                              </div>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <RoleBadge role={invitation.role as Role} />
                        </TableCell>
                        <TableCell className="text-muted-foreground text-sm">
                          {formatDate(invitation.created_at)}
                        </TableCell>
                        <TableCell className="text-sm">
                          {isExpired ? (
                            <Badge variant="destructive" className="text-xs">Expired</Badge>
                          ) : (
                            <span className="text-muted-foreground">{formatDate(invitation.expires_at)}</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 hover:bg-muted/50"
                              onClick={() => handleResendInvitation(invitation.id)}
                              disabled={isProcessing || isResending}
                              title="Resend invitation"
                            >
                              {isProcessing && isResending ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <RefreshCw className="h-4 w-4" />
                              )}
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 hover:bg-destructive/10 text-muted-foreground hover:text-destructive"
                              onClick={() => handleRevokeInvitation(invitation.id)}
                              disabled={isProcessing || isRevoking}
                              title="Revoke invitation"
                            >
                              {isProcessing && isRevoking ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <X className="h-4 w-4" />
                              )}
                            </Button>
                          </div>
                        </TableCell>
                      </motion.tr>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </motion.div>
      )}
    </div>
  );
}
